/**
 * CompanyCodeTab — extracted from CustomerForm.js for readability.
 * Receives everything via a single `ctx` bag so the parent keeps
 * full ownership of state management.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Building2, Plus, Edit, Trash2 } from 'lucide-react';

const CompanyCodeTab = ({ ctx }) => {
  const {
    companyCodeData,
    ccFormData,
    setCcFormData,
    showCcDialog,
    setShowCcDialog,
    editingCcIndex,
    setEditingCcIndex,
    companyCodes,
    termsOfPayment,
    handleAddCompanyCode,
    handleEditCompanyCode,
    handleDeleteCompanyCode,
    readOnly,
    setActiveTab,
  } = ctx;

  return (
    <>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Company Code Assignments</h3>
                      <p className="text-sm text-gray-600">
                        {companyCodeData.length} Company Code(s) added
                      </p>
                    </div>
                    {!readOnly && (
                      <Button
                        type="button"
                        onClick={() => setShowCcDialog(true)}
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Company Code
                      </Button>
                    )}
                  </div>

                  {/* Company Code Table */}
                  {companyCodeData.length > 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Code</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reconciliation Account</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Terms of Payment</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Payment History</th>
                            {!readOnly && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {companyCodeData.map((cc, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{cc.company_code_id}</td>
                              <td className="px-4 py-3">{cc.reconciliation_acct}</td>
                              <td className="px-4 py-3">{cc.terms_of_payment_fi}</td>
                              <td className="px-4 py-3 text-center">
                                {cc.record_pay_history ? '✓' : '–'}
                              </td>
                              {!readOnly && (
                                <td className="px-4 py-3 text-right space-x-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEditCompanyCode(index)}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDeleteCompanyCode(index)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                      <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">No Company Codes added yet</p>
                      <p className="text-sm text-gray-500 mt-1">Click "Add Company Code" to get started</p>
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setActiveTab('general')}>
                      ← Back to General
                    </Button>
                    <Button type="button" onClick={() => setActiveTab('sales')} className="bg-orange-600 hover:bg-orange-700">
                      Continue to Sales & Distribution →
                    </Button>
                  </div>
                </div>

                {/* Company Code Dialog */}
                {showCcDialog && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
                      <h2 className="text-xl font-semibold mb-4">
                        {editingCcIndex !== null ? 'Edit' : 'Add'} Company Code
                      </h2>
                      <div className="space-y-4">
                        <div>
                          <Label>Company Code *</Label>
                          <Select
                            value={ccFormData.company_code_id}
                            onValueChange={(value) => setCcFormData({ ...ccFormData, company_code_id: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Company Code" />
                            </SelectTrigger>
                            <SelectContent>
                              {companyCodes.map((cc) => (
                                <SelectItem key={cc.company_code_id} value={cc.company_code_id}>
                                  {cc.company_code_id} - {cc.company_code_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Reconciliation Account *</Label>
                          <Input
                            value={ccFormData.reconciliation_acct}
                            onChange={(e) => setCcFormData({ ...ccFormData, reconciliation_acct: e.target.value.toUpperCase() })}
                            placeholder="e.g., 140000"
                            maxLength={10}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Terms of Payment *</Label>
                          <Select
                            value={ccFormData.terms_of_payment_fi}
                            onValueChange={(value) => setCcFormData({ ...ccFormData, terms_of_payment_fi: value })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Payment Terms" />
                            </SelectTrigger>
                            <SelectContent>
                              {termsOfPayment.map((term) => (
                                <SelectItem key={term.payment_key} value={term.payment_key}>
                                  {term.payment_key} - {term.sales_text}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="cc_pay_history"
                            checked={ccFormData.record_pay_history}
                            onCheckedChange={(checked) => setCcFormData({ ...ccFormData, record_pay_history: checked })}
                          />
                          <Label htmlFor="cc_pay_history" className="cursor-pointer">
                            Record Payment History
                          </Label>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowCcDialog(false);
                            setEditingCcIndex(null);
                            setCcFormData({ company_code_id: '', reconciliation_acct: '', terms_of_payment_fi: '', record_pay_history: false });
                          }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={handleAddCompanyCode}
                          className="flex-1 bg-orange-600 hover:bg-orange-700"
                        >
                          {editingCcIndex !== null ? 'Update' : 'Add'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
    </>
  );
};

export default CompanyCodeTab;
